import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, DownloadBoldOutlineIcon, Loader, Typography } from '@rappi/rests-commons-front';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGlobal } from '../../context/global/global.context';
import { GlobalActions } from '../../context/global/types';
import { mergeFilesIntoPdf } from '../../utils/merge-files-into-pdf';
import { Content, ErrorMessage, Rule, RulesContainer, Title, Wrapper } from './styles';
export const FileUploader = ({ file, onFileUpload, onError, rules }) => {
    var _a;
    const { t } = useTranslation();
    const { dispatch } = useGlobal();
    const fileInputRef = useRef(null);
    const [fileState, setFileState] = useState(file);
    const [isDragging, setIsDragging] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isMergingFiles, setMergingFiles] = useState(false);
    const [fileUrlMerged, setFileUrlMerged] = useState(null);
    useEffect(() => {
        setFileState(file);
    }, [file]);
    const handleMergefiles = (files) => {
        setMergingFiles(true);
        void mergeFilesIntoPdf(t('menuGenerator.sidebar.form.fileInput.multiple.fileName'), files)
            .then(({ url, file: mergedFile }) => {
            handleFileUpload(mergedFile);
            setFileUrlMerged(url);
        })
            .catch(() => {
            dispatch({
                type: GlobalActions.showNotification,
                payload: {
                    type: 'error',
                    message: t('menuGenerator.sidebar.form.fileInput.multiple.error'),
                },
            });
        })
            .finally(() => {
            setMergingFiles(false);
        });
    };
    const handleFileUpload = useCallback((file) => {
        setFileState(null);
        setErrorMessage('');
        onError === null || onError === void 0 ? void 0 : onError('');
        if (!file) {
            const message = t('menuGenerator.sidebar.form.fileInput.rules.invalid.formats');
            setErrorMessage(message);
            onError === null || onError === void 0 ? void 0 : onError(message);
            return;
        }
        setFileState(file);
        if ((rules === null || rules === void 0 ? void 0 : rules.allowedTypes) && !rules.allowedTypes.includes(file.type)) {
            const message = t('menuGenerator.sidebar.form.fileInput.rules.invalid.formats');
            setErrorMessage(message);
            onError === null || onError === void 0 ? void 0 : onError(message);
            return;
        }
        if ((rules === null || rules === void 0 ? void 0 : rules.maxSizeMb) && file.size >= rules.maxSizeMb * 1024 * 1024) {
            const message = t('menuGenerator.sidebar.form.fileInput.rules.invalid.maxSize');
            setErrorMessage(message);
            onError === null || onError === void 0 ? void 0 : onError(message);
            return;
        }
        onFileUpload(file);
    }, [onFileUpload, onError, rules]);
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        const filesArray = Array.from(files);
        setFileUrlMerged(null);
        if (filesArray.length === 1) {
            handleFileUpload(filesArray[0]);
            return;
        }
        handleMergefiles(filesArray);
    }, [handleFileUpload]);
    const handleSelectFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    const handleFileChange = (e) => {
        const files = e.target.files || [];
        const filesArray = Array.from(files);
        setFileUrlMerged(null);
        if (filesArray.length === 1) {
            handleFileUpload(filesArray[0]);
            return;
        }
        handleMergefiles(filesArray);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    return (_jsx(Wrapper, { "$isDragging": isDragging, "$error": Boolean(errorMessage), onDragEnter: handleDragEnter, onDragOver: handleDragEnter, onDragLeave: handleDragLeave, onDrop: handleDrop, children: _jsxs(Content, { "$isDragging": isDragging, children: [isMergingFiles && _jsx(Loader, { show: true, fullscreen: false }), isDragging ? (_jsxs(_Fragment, { children: [_jsx(DownloadBoldOutlineIcon, {}), _jsx(Title, { children: t('menuGenerator.sidebar.form.fileInput.dropLabel') })] })) : (_jsxs(_Fragment, { children: [fileState ? (_jsxs(_Fragment, { children: [_jsx(Title, { "$semibold": Boolean(fileState), "$error": Boolean(errorMessage), children: fileUrlMerged ? (_jsx(Link, { to: fileUrlMerged, target: "_blank", style: { color: 'inherit' }, children: fileState.name })) : (fileState.name) }), errorMessage ? (_jsxs(ErrorMessage, { children: [errorMessage, " ", (fileState.size / 1024 / 1024).toFixed(2), " MB"] })) : (_jsx(Typography, { component: "body", children: t('menuGenerator.sidebar.form.fileInput.successLabel') }))] })) : (_jsx(Title, { children: t('menuGenerator.sidebar.form.fileInput.label') })), _jsx(Button, { fullWidth: true, type: "button", variant: fileState ? 'secondary' : 'primary', text: fileState
                                ? t('menuGenerator.sidebar.form.fileInput.replace')
                                : t('menuGenerator.sidebar.form.fileInput.select'), onClick: handleSelectFile }), _jsx("input", { multiple: true, ref: fileInputRef, type: "file", onChange: handleFileChange, style: { display: 'none' }, accept: (_a = rules === null || rules === void 0 ? void 0 : rules.allowedTypes) === null || _a === void 0 ? void 0 : _a.join(',') }), (!fileState || Boolean(errorMessage)) && rules && (_jsxs(RulesContainer, { children: [rules.allowedTypes && (_jsxs(Rule, { children: [_jsxs("span", { children: [t('menuGenerator.sidebar.form.fileInput.rules.label.formats'), ": "] }), _jsx("span", { children: rules.allowedTypes
                                                .map((type) => type.split('/')[1])
                                                .join(', ')
                                                .toUpperCase() })] })), rules.maxSizeMb && (_jsxs(Rule, { children: [_jsxs("span", { children: [t('menuGenerator.sidebar.form.fileInput.rules.label.maxSize'), ": "] }), _jsxs("span", { children: [rules.maxSizeMb, " MB"] })] }))] }))] }))] }) }));
};
